import { useEffect, useState } from "react";
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Calculator,
  CalendarRange,
  ChartPie,
  Plus,
  Trash2,
  TrendingDown,
  TrendingUp,
} from "lucide-react";

import { ApiError } from "@/lib/api";
import { MONTHS } from "@/components/budget-grid";
import { useBudgetLines, useCurrentBudget, type BudgetLine, type LineKind, type NewLine } from "@/lib/budget";
import { useFinance } from "@/lib/hooks";
import { money } from "@/lib/format";
import { tagStyle } from "@/lib/meta";
import { cn } from "@/lib/utils";
import { useNavigate, useRoute } from "@/components/app-shell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

const KIND_META: Record<LineKind, { label: string; icon: typeof TrendingUp; hint: string }> = {
  income: { label: "Income", icon: TrendingUp, hint: "Money coming in — pensja, refunds." },
  expense: { label: "Expense", icon: TrendingDown, hint: "Money going out — telefon, gaz, woda." },
  formula: {
    label: "Formula",
    icon: Calculator,
    hint: "Computed: [gaz] + [woda] for named lines, sum(#dom) for a tag, or sum(#expense) / sum(#income) for every line of that kind in this budget.",
  },
};

function message(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return "Something went wrong. Please try again.";
}

/**
 * One budget: what it is made of. Lines are added, edited and removed here;
 * the Overview is for recording what was actually paid.
 */
export function FinanceBudgetPage() {
  const { budgets, tags } = useFinance();
  const route = useRoute();
  const [currentId, selectBudget] = useCurrentBudget(budgets);

  // The URL wins: opening /finance/budgets/<id> shows that budget, whatever was
  // last selected, and makes it current so the Overview follows.
  const routeId = route.param && budgets.some((b) => b.id === route.param) ? route.param : null;
  const budgetId = routeId ?? currentId;
  const budget = budgets.find((b) => b.id === budgetId);

  useEffect(() => {
    if (routeId && routeId !== currentId) selectBudget(routeId);
  }, [routeId, currentId, selectBudget]);
  const { lines, loading, addLine, updateLine, deleteLine, reorder, setPlan, clearPlan } = useBudgetLines(budgetId);
  const [openPlans, setOpenPlans] = useState<string | null>(null);
  const navigate = useNavigate();

  const [kind, setKind] = useState<LineKind>("expense");
  const [name, setName] = useState("");
  const [tagId, setTagId] = useState("");
  const [planned, setPlanned] = useState("");
  const [formula, setFormula] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const guard = async (fn: () => Promise<void>) => {
    setError("");
    try {
      await fn();
    } catch (err) {
      setError(message(err));
    }
  };

  const submit = async () => {
    if (!name.trim() || busy) return;
    if (kind === "formula" && !formula.trim()) return;
    setBusy(true);
    await guard(async () => {
      const line: NewLine = { kind, name: name.trim() };
      if (kind === "formula") {
        line.formula = formula.trim();
      } else {
        if (tagId) line.tagId = tagId;
        if (planned.trim()) line.planned = Number(planned.replace(",", "."));
      }
      await addLine(line);
      setName("");
      setPlanned("");
      setFormula("");
    });
    setBusy(false);
  };

  const move = async (index: number, delta: number) => {
    const next = [...lines];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target]!, next[index]!];
    await guard(() => reorder(next.map((l) => l.id)));
  };

  if (!budget) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" className="gap-1" onClick={() => navigate("finance-config")}>
          <ArrowLeft className="size-4" /> Configuration
        </Button>
        <p className="text-sm text-muted-foreground">Pick a budget in Configuration.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="ghost" size="sm" className="gap-1" onClick={() => navigate("finance-config")}>
          <ArrowLeft className="size-4" /> Configuration
        </Button>
        <Button variant="outline" size="sm" className="gap-1" onClick={() => navigate("finance-dashboard")}>
          <ChartPie className="size-4" /> Open overview
        </Button>
      </div>

      <div>
        <h2 className="text-xl font-bold">{budget.name}</h2>
        <p className="text-sm text-muted-foreground">
          The lines this budget is made of. Amounts are recorded month by month on the Overview.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Lines</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            {!loading && lines.length === 0 && (
              <p className="py-2 text-sm text-muted-foreground">
                No lines yet. These become the rows of the year grid.
              </p>
            )}

            {lines.map((line, i) => {
              const Icon = KIND_META[line.kind].icon;
              const tag = tags.find((t) => t.id === line.tagId);
              return (
                <div key={line.id} className="flex flex-wrap items-center gap-2 rounded-lg border p-2">
                  <Icon
                    className={cn(
                      "size-4 shrink-0",
                      line.kind === "income" && "text-emerald-600",
                      line.kind === "expense" && "text-muted-foreground",
                      line.kind === "formula" && "text-violet-500",
                    )}
                  />
                  <Input
                    className="h-8 w-40"
                    aria-label={`Rename ${line.name}`}
                    defaultValue={line.name}
                    onFocus={(e) => e.currentTarget.select()}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v && v !== line.name) void guard(() => updateLine(line.id, { name: v }));
                    }}
                    onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                  />

                  {line.kind === "expense" && (
                    <select
                      className="h-8 rounded-md border border-input bg-background px-2 text-sm text-foreground shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                      aria-label={`Tag for ${line.name}`}
                      value={line.tagId ?? ""}
                      onChange={(e) => void guard(() => updateLine(line.id, { tagId: e.target.value || null }))}
                    >
                      <option value="">No tag</option>
                      {tags.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  )}
                  {tag && <span className={cn("size-2 rounded-full", tagStyle(tag.color).dot)} />}

                  {line.kind === "formula" ? (
                    <Input
                      className="h-8 min-w-48 flex-1 font-mono text-xs"
                      aria-label={`Formula for ${line.name}`}
                      defaultValue={line.formulaText ?? ""}
                      onFocus={(e) => e.currentTarget.select()}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (v && v !== line.formulaText) void guard(() => updateLine(line.id, { formula: v }));
                      }}
                      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                    />
                  ) : (
                    <Input
                      className="h-8 w-24"
                      inputMode="decimal"
                      aria-label={`Planned amount for ${line.name}`}
                      placeholder="plan"
                      defaultValue={line.planned != null ? String(line.planned) : ""}
                      onFocus={(e) => e.currentTarget.select()}
                      onBlur={(e) => {
                        const raw = e.target.value.trim().replace(",", ".");
                        const next = raw === "" ? null : Number(raw);
                        if (!Number.isNaN(next) && next !== line.planned) {
                          void guard(() => updateLine(line.id, { planned: next }));
                        }
                      }}
                      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                    />
                  )}

                  {line.kind !== "formula" && (
                    <Button
                      size="sm"
                      variant={openPlans === line.id ? "default" : "ghost"}
                      className="h-8 gap-1 px-2 text-xs"
                      aria-label={`Per-month plan for ${line.name}`}
                      onClick={() => setOpenPlans(openPlans === line.id ? null : line.id)}
                    >
                      <CalendarRange className="size-3.5" />
                      {line.planOverrides && Object.keys(line.planOverrides).length > 0
                        ? `${Object.keys(line.planOverrides).length} month${Object.keys(line.planOverrides).length === 1 ? "" : "s"}`
                        : "per month"}
                    </Button>
                  )}

                  <span className="flex-1" />
                  {line.planned != null && (
                    <span className="hidden whitespace-nowrap text-xs text-muted-foreground sm:inline">
                      {money(line.planned)}
                    </span>
                  )}
                  <Button size="icon" variant="ghost" aria-label={`Move ${line.name} up`} onClick={() => void move(i, -1)}>
                    <ArrowUp className="size-3.5" />
                  </Button>
                  <Button size="icon" variant="ghost" aria-label={`Move ${line.name} down`} onClick={() => void move(i, 1)}>
                    <ArrowDown className="size-3.5" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="text-destructive"
                    aria-label={`Delete ${line.name}`}
                    onClick={() => void guard(() => deleteLine(line.id))}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>

                  {openPlans === line.id && (
                    <MonthPlans
                      line={line}
                      onSet={(month, amount) => guard(() => setPlan(line.id, month, amount))}
                      onClear={(month) => guard(() => clearPlan(line.id, month))}
                    />
                  )}
                </div>
              );
            })}
          </div>

          <div className="space-y-2 rounded-lg border border-dashed p-3">
            <div className="flex flex-wrap gap-1">
              {(Object.keys(KIND_META) as LineKind[]).map((k) => (
                <Button key={k} size="sm" variant={kind === k ? "default" : "outline"} onClick={() => setKind(k)}>
                  {KIND_META[k].label}
                </Button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">{KIND_META[kind].hint}</p>

            <div className="flex flex-wrap gap-2">
              <Input
                className="min-w-40 flex-1"
                placeholder="Line name…"
                aria-label="New line name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && void submit()}
              />
              {kind === "formula" ? (
                <Input
                  className="min-w-52 flex-1 font-mono"
                  placeholder="sum(#expense) * 0.5"
                  aria-label="New line formula"
                  value={formula}
                  onChange={(e) => setFormula(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && void submit()}
                />
              ) : (
                <>
                  <select
                    className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                    aria-label="Tag for the new line"
                    value={tagId}
                    onChange={(e) => setTagId(e.target.value)}
                  >
                    <option value="">No tag</option>
                    {tags.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  <Input
                    className="w-28"
                    inputMode="decimal"
                    placeholder="plan"
                    aria-label="Planned amount for the new line"
                    value={planned}
                    onChange={(e) => setPlanned(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && void submit()}
                  />
                </>
              )}
              <Button aria-label="Add line" onClick={() => void submit()} disabled={!name.trim() || busy} className="gap-1">
                <Plus className="size-4" /> Add
              </Button>
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * The twelve monthly plans for one line. A blank box inherits the line default,
 * so a once-a-year payment is a line with no default and a single month filled
 * in — and a varying one is a default with a few months overridden.
 */
function MonthPlans({
  line,
  onSet,
  onClear,
}: {
  line: BudgetLine;
  onSet: (month: number, amount: number | null) => Promise<void>;
  onClear: (month: number) => Promise<void>;
}) {
  const overrides = line.planOverrides ?? {};

  const commit = async (month: number, raw: string) => {
    const trimmed = raw.trim().replace(",", ".");
    if (trimmed === "") {
      if (overrides[String(month)] !== undefined) await onClear(month);
      return;
    }
    const amount = Number(trimmed);
    if (Number.isNaN(amount) || amount === overrides[String(month)]) return;
    await onSet(month, amount);
  };

  return (
    <div className="mt-2 w-full rounded-lg border border-dashed p-3">
      <p className="mb-2 text-xs text-muted-foreground">
        Plan per month. Leave a box empty to use the default
        {line.planned != null ? ` (${money(line.planned)})` : " (none)"} — so a once-a-year payment is just the one
        month filled in.
      </p>
      <div className="grid grid-cols-6 gap-1.5 sm:grid-cols-12">
        {MONTHS.map((label, i) => {
          const month = i + 1;
          const override = overrides[String(month)];
          return (
            <label key={label} className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase text-muted-foreground">{label}</span>
              <Input
                className="h-8 px-1 text-center text-xs tabular-nums"
                inputMode="decimal"
                aria-label={`${line.name} plan for ${label}`}
                placeholder={line.planned != null ? String(line.planned) : "—"}
                defaultValue={override != null ? String(override) : ""}
                onFocus={(e) => e.currentTarget.select()}
                onBlur={(e) => void commit(month, e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
              />
            </label>
          );
        })}
      </div>
    </div>
  );
}
